"use client";

import Analytics from "@/app/_components/analytics";
import { KYCVerification } from "@/components/WeeklyRewards/KYCVerification";
import { FlaggedAccountMessage } from "@/components/WeeklyRewards/Redemption/FlaggedAccountMessage";
import { RewardContentSection } from "@/components/WeeklyRewards/RewardContentSection";
import { RewardsDailyGems } from "@/components/WeeklyRewards/RewardsDailyGems";
import { RewardsFAQsSection } from "@/components/WeeklyRewards/RewardsFAQsSection";
import { RewardsOnboarding } from "@/components/WeeklyRewards/RewardsOnboarding";
import { WeeklyRewards } from "@/components/WeeklyRewards/WeeklyRewards";
import { WeeklyRewardsBackground } from "@/components/WeeklyRewards/WeeklyRewardsBackground";
import { WeeklyRewardsDevPanel } from "@/components/WeeklyRewards/WeeklyRewardsDevPanel";
import { useWeeklyRewards } from "@/components/WeeklyRewards/WeeklyRewardsProvider";
import SEO from "@/components/core/Seo";
import { MainContent } from "@/components/layout/MainContent";
import { usePassportProvider } from "@/context";
import { usePreferences } from "@/context/PreferencesProvider";
import { ConnectionProvider } from "@/hooks/connection";
import { RenderOnceInView } from "@ui-kit";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useSearchParams } from "next/navigation";
import { Suspense, useEffect, useMemo } from "react";

function RewardsContent() {
  const { data: preferencesData, update: updatePreferences } = usePreferences();
  const { weeklyRewardsDevPanelEnabled } = useFlags();
  const { passportState } = usePassportProvider();
  const { eligibility, isEligibilityLoading, hasFreeEntry, isAccountFlagged } =
    useWeeklyRewards();

  const isKycRequired =
    eligibility !== undefined && !eligibility?.rules.is_kyc_exempt;

  useEffect(() => {
    if (preferencesData?.hide_activation_badges.rewards === false) {
      updatePreferences({
        hide_activation_badges: {
          ...preferencesData?.hide_activation_badges,
          rewards: true,
        },
      });
    }
  }, [preferencesData, updatePreferences]);

  if (
    passportState.ready &&
    passportState.authenticated &&
    !eligibility?.is_eligible &&
    !isEligibilityLoading &&
    (isAccountFlagged || isKycRequired)
  ) {
    return (
      <ConnectionProvider>
        <WeeklyRewardsBackground />
        {isKycRequired ? (
          <KYCVerification />
        ) : (
          <RewardsOnboarding hasFreeEntry={hasFreeEntry} />
        )}
        {isAccountFlagged && <FlaggedAccountMessage />}
      </ConnectionProvider>
    );
  }

  return (
    <ConnectionProvider>
      <WeeklyRewardsBackground />
      {passportState.ready && passportState.authenticated && isKycRequired ? (
        <RenderOnceInView placeHolderHeight="500px">
          <KYCVerification />
        </RenderOnceInView>
      ) : (
        <RenderOnceInView placeHolderHeight="500px">
          <WeeklyRewards />
        </RenderOnceInView>
      )}
      {!isEligibilityLoading && (
        <>
          <RenderOnceInView placeHolderHeight="100px">
            <RewardContentSection />
          </RenderOnceInView>
          <RenderOnceInView placeHolderHeight="50px">
            <RewardsFAQsSection />
          </RenderOnceInView>
        </>
      )}

      {weeklyRewardsDevPanelEnabled && <WeeklyRewardsDevPanel />}
      <RenderOnceInView placeHolderHeight="100px">
        <RewardsDailyGems />
      </RenderOnceInView>
    </ConnectionProvider>
  );
}

function RewardsPageContent() {
  const searchParams = useSearchParams();
  const share = searchParams?.get("share");

  const rootUrl = "https://play.immutable.com";

  const { decodedRarity, imagePath } = useMemo(() => {
    let rarity: string | undefined;
    let image = `${rootUrl}/images/rewards/keys/default.webp`;

    if (typeof share === "string") {
      try {
        rarity = atob(share);
      } catch (e) {
        console.warn("Failed to decode share parameter, using as-is", e);
        rarity = share;
      }

      if (rarity) {
        const rarityLower = rarity.toLowerCase();
        image = `${rootUrl}/images/rewards/keys/${rarityLower}.png`;
      }
    }

    return { decodedRarity: rarity, imagePath: image };
  }, [share]);

  return (
    <>
      <Analytics screen="Rewards" userJourney="Rewards" />
      <SEO
        description="Join Immutable Play's Perpetual Rewards and unlock prizes each week!"
        title={
          decodedRarity
            ? `I just won a ${decodedRarity} Key! | Immutable Play`
            : "Rewards | Immutable Play"
        }
        url={`${rootUrl}/rewards${share ? `?share=${share}` : ""}`}
        image={imagePath}
        imageAlt="Immutable Play Rewards"
        twitterCard="summary_large_image"
        twitterLabel1={decodedRarity ? "Reward Rarity" : undefined}
        twitterData1={decodedRarity}
        additionalPropertyTags={[
          { property: "og:image:width", content: "1200" },
          { property: "og:image:height", content: "630" },
          { property: "og:site_name", content: "Immutable Play" },
          { property: "twitter:site", content: "@Immutable" },
        ]}
      />
      <MainContent>
        <RewardsContent />
      </MainContent>
    </>
  );
}

export default function RewardsPage() {
  return (
    <Suspense fallback={null}>
      <RewardsPageContent />
    </Suspense>
  );
}
